import { computedFrom } from 'aurelia-binding';
import { autoinject } from 'aurelia-framework';

import { TableColumnDefinition } from '~/components/acurity-table/acurity-table';
import { DataCompareTable } from '~/components/data-compare-table/data-compare-table';
import { DrilldownCommonModel } from '~/components/dialog/dialog-with-tabs/drilldown-model';
import {
	SimpleDialog,
	SimpleDialogType,
} from '~/components/dialog/simple-dialog';
import { Notification } from '~/components/notification/notification';
import { Address } from '~/converters/address-format';
// tslint:disable-next-line no-circular-imports
import { ATOInterfaces } from '~/forms/ato-interfaces/ato-interfaces';
import {
	ATOInterfacesModel,
	DataObject,
} from '~/forms/ato-interfaces/models/ato-interfaces-model';
import { ColumnDefinitionMap } from '~/models/column-definition';
import { DataTypeEnum } from '~/models/data-type';
import { Converters } from '~/utils/converters';
import { FetchUtils } from '~/utils/fetch-utils';
import { JQueryHelper } from '~/utils/jquery-helper';

@autoinject
export class DataCompareTab extends DrilldownCommonModel {
	private requestId: number;
	private reviewResponseButtonDisabled = true;
	public selectAllDisabled = true;
	private clearSelectionDisabled = true; // disabled by default because no rows selected on page load
	private updateSelectedDisabled = true; // disabled by default because no rows selected on page load
	public dataCompareTable: DataCompareTable;
	public model: ATOInterfacesModel;
	private tableColumns: TableColumnDefinition[];

	constructor(
		template: string,
		data: ATOInterfacesModel,
		tableColumnDefinitions: ColumnDefinitionMap,
		readonly form?: ATOInterfaces,
	) {
		super(template, data, tableColumnDefinitions);
		this.requestId = this.model.atoRequest.TQi_Identity;
		if (this.dataExistsFlag === 'D' || this.dataExistsFlag === 'E') {
			this.reviewResponseButtonDisabled = false;
		}

		JQueryHelper.whenReady($('#maas-data-compare-table')).then((obj) => {
			if (this.dataCompareTable) {
				this.dataCompareTable.onTableInitialised().then(() => {
					this.showDateColumns();
					this.initSelectButtons();
				});
			}
		});

		this.tableColumns = [
			{
				name: 'information',
				label: 'Information',
				dataType: { name: DataTypeEnum.String },
			},
			{
				name: 'currentData',
				label: 'Current Data',
				dataType: { name: DataTypeEnum.String },
				render: this.formatType,
			},
			{
				name: 'currentModifiedDate',
				label: 'Last Updated',
				dataType: { name: DataTypeEnum.String },
				render: this.formatType,
				hiddenFrom: [],
			},
			{
				name: 'atoDataReturned',
				label: 'ATO Data Returned',
				dataType: { name: DataTypeEnum.String },
				render: this.formatType,
			},
			{
				name: 'atoStartDate',
				label: 'ATO Notified Date',
				dataType: { name: DataTypeEnum.String },
				render: this.formatType,
				hiddenFrom: [],
			},
			{
				name: 'updateEnabled',
				label: 'Update',
				dataType: { name: DataTypeEnum.Boolean },
				render: this.formatType,
			},
		];
	}

	@computedFrom('model.atoResponse.TOz_Data_Exists')
	public get dataExistsFlag(): string {
		if (this.model.atoResponse && this.model.atoResponse.TOz_Data_Exists) {
			return this.model.atoResponse.TOz_Data_Exists.trim();
		} else {
			return '';
		}
	}

	private readonly formatType = (dataObject: DataObject): string => {
		const dataType =
			dataObject && dataObject.dataType ? dataObject.dataType.name : '';
		switch (dataType) {
			case 'String':
				return String(dataObject.values);
			case 'Date':
				return Converters.dateFormat(String(dataObject.values));
			case 'Character':
				return Converters.existsNoneUnconfirmedFormat(
					String(dataObject.values),
				);
			case 'Address':
				return Converters.addressFormat(<Address>dataObject.values);
			default:
				if (typeof dataObject === 'boolean') {
					return String(dataObject);
				}
				return dataObject &&
					dataObject.values &&
					String(dataObject.values).length > 0
					? String(dataObject.values)
					: '';
		}
	};

	private showDateColumns() {
		const dataTable = $('#' + this.dataCompareTable.tableId).DataTable();

		for (const rowIndex in this.model.comparedDataList) {
			// only need to show date columns if table contains at least one ATO start date
			if (this.model.comparedDataList[rowIndex].atoStartDate) {
				for (const columnIndex in this.dataCompareTable.tableColumns) {
					if (
						this.dataCompareTable.tableColumns[columnIndex].name ===
							'currentModifiedDate' ||
						this.dataCompareTable.tableColumns[columnIndex].name ===
							'atoStartDate'
					) {
						dataTable.column(columnIndex).visible(true);
					}
				}
				break;
			}
		}
	}

	private initSelectButtons() {
		const $table = $('#' + this.dataCompareTable.tableId);
		const $tableRows = $table.find('tbody tr');
		const dataTable = $table.DataTable();

		this.selectAllDisabled =
			$tableRows.find('td.select-checkbox:not(.disabled)').length === 0;

		dataTable.on('select deselect', (e, dt, selectType, indexes) => {
			if (selectType === 'row') {
				const selectedRowCount = dataTable
					.rows({ selected: true })
					.count();
				this.clearSelectionDisabled = selectedRowCount === 0;
				this.updateSelectedDisabled = selectedRowCount === 0;
				this.selectAllDisabled =
					$tableRows.find('td.select-checkbox:not(.disabled)')
						.length === selectedRowCount;
			}
		});
	}

	public selectAll() {
		const $table = $('#' + this.dataCompareTable.tableId);
		$table
			.find('td.select-checkbox:not(.disabled)')
			.parent()
			.each((_, trElem) => {
				$table.DataTable().rows(trElem).select();
			});

		this.selectAllDisabled = true;
		this.clearSelectionDisabled = false;
		this.updateSelectedDisabled = false;
	}

	public clearSelection() {
		$('#' + this.dataCompareTable.tableId)
			.DataTable()
			.rows()
			.deselect();
		this.selectAllDisabled = false;
		this.clearSelectionDisabled = true;
		this.updateSelectedDisabled = true;
	}

	public updateSelected() {
		const selectedRows = $('#' + this.dataCompareTable.tableId)
			.DataTable()
			.rows({ selected: true });
		if (selectedRows.count() === 0) {
			return;
		}

		const rowsToUpdate: string[] = [];
		for (const id of selectedRows[0]) {
			rowsToUpdate.push(this.model.comparedDataList[id].information);
		}

		if (rowsToUpdate.indexOf('Postal Address') > -1) {
			this.disableClickableElements();
			this.form.api
				.getHaltCorrespondence(this.model.membership)
				.then((haltCorrespondence) => {
					if (haltCorrespondence) {
						this.showResumeCorrespondenceDialog(rowsToUpdate);
					} else {
						this.updateSelectedData(rowsToUpdate, true);
					}
				})
				.catch((error) => {
					this.handleWebError(
						'ato-interfaces:failed-to-get-halt-correspondence-flag',
						error,
					);
					this.reInitClickableElements();
				});
		} else {
			this.updateSelectedData(rowsToUpdate, true);
		}
	}

	private updateSelectedData(
		rowsToUpdate: string[],
		resumeCorrespondence: boolean,
	) {
		this.disableClickableElements();
		this.form.api
			.updateSelectedData(
				this.requestId,
				rowsToUpdate,
				resumeCorrespondence,
			)
			.then((comparedDataList) => {
				this.dataCompareTable.refreshNormalTable(comparedDataList);
				this.reInitClickableElements();
				this.showDateColumns();
			})
			.catch((error) => {
				this.handleWebError(
					'errors.failed-to-update-selected-data',
					error,
				);
				this.reInitClickableElements();
			});
	}

	public showResumeCorrespondenceDialog(rowsToUpdate: string[]) {
		const dialogModel = this.form.dialogUtils.getSimpleDialogModel({
			simpleDialogType: SimpleDialogType.WARNING,
			bodyText: this.form.i18n.tr('warnings.resume-correspondence'),
		});
		this.form.dialogUtils
			.showCustomDialog(SimpleDialog, dialogModel)
			.whenClosed((value) => {
				this.updateSelectedData(rowsToUpdate, !value.wasCancelled);
			});
	}

	public showResponseReviewedDialog() {
		this.form.showResponseReviewedDialog(this);
	}

	public disableClickableElements() {
		this.reviewResponseButtonDisabled = true;
		this.selectAllDisabled = true;
		this.clearSelectionDisabled = true;
		this.updateSelectedDisabled = true;
		$('.a-table-overlay').addClass('a-table-overlay--shown');
		$('[data-toggle="tooltip"]').tooltip('hide');
	}

	public reInitClickableElements() {
		if (this.dataExistsFlag === 'D' || this.dataExistsFlag === 'E') {
			this.reviewResponseButtonDisabled = false;
		}
		this.initSelectButtons();
		this.clearSelection();

		// disabled by default because no rows selected when reinitialising elements
		this.clearSelectionDisabled = true;
		this.updateSelectedDisabled = true;
		$('.a-table-overlay').removeClass('a-table-overlay--shown');
	}

	private handleWebError(message, error) {
		Notification.error(
			this.form.i18n.tr('utils.message-type', {
				message: '$t(' + message + ')',
				type: FetchUtils.normalizeError(error),
			}),
		);
	}
}
